import React, { Suspense, lazy } from 'react';
import '@/App.css';
import '@/index.css';
import { BrowserRouter, Routes, Route, Link } from 'react-router-dom';

// Context
import { ClubInfoProvider } from '@/context/ClubInfoContext';

// Components (immer eager - werden auf jeder Seite gebraucht)
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import BackToTop from '@/components/BackToTop';
import ScrollToTop from '@/components/ScrollToTop';

// Home wird eager geladen (LCP-relevant: erste Seite, die Besucher sehen).
import Home from '@/components/pages/Home';

// Alle anderen Seiten werden bei Bedarf nachgeladen (Code-Splitting).
// Spart ~70% Initial-Bundle: Admin (recharts), Gallery, Facilities-Karte
// etc. laden nur, wenn sie auch besucht werden.
const About = lazy(() => import('@/components/pages/About'));
const RidingLessons = lazy(() => import('@/components/pages/RidingLessons'));
const Tournaments = lazy(() => import('@/components/pages/Tournaments'));
const Activities = lazy(() => import('@/components/pages/Activities'));
const Gallery = lazy(() => import('@/components/pages/Gallery'));
const Facilities = lazy(() => import('@/components/pages/Facilities'));
const Membership = lazy(() => import('@/components/pages/Membership'));
const Board = lazy(() => import('@/components/pages/Board'));
const Sponsors = lazy(() => import('@/components/pages/Sponsors'));
const Contact = lazy(() => import('@/components/pages/Contact'));
const Documents = lazy(() => import('@/components/pages/Documents'));
const Imprint = lazy(() => import('@/components/pages/Imprint'));
const Privacy = lazy(() => import('@/components/pages/Privacy'));
const Admin = lazy(() => import('@/components/pages/Admin'));
const NewsDetail = lazy(() => import('@/components/pages/NewsDetail'));
const LessonOfferDetail = lazy(() => import('@/components/pages/LessonOfferDetail'));
const NotFound = lazy(() => import('@/components/pages/NotFound'));

// Toaster for notifications
import { Toaster } from '@/components/ui/sonner';

const RouteFallback = () => (
  <div className="min-h-[50vh] flex items-center justify-center text-muted-foreground">Lädt …</div>
);

function App() {
  return (
    <div className="App min-h-[100dvh] flex flex-col">
      <ClubInfoProvider>
        <BrowserRouter>
          <ScrollToTop />
          {/* Skip-Link fuer Tastatur-/Screenreader-Nutzer (WCAG 2.4.1). */}
          <a
            href="#main"
            className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[100] focus:px-4 focus:py-2 focus:rounded focus:bg-primary focus:text-primary-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          >
            Zum Hauptinhalt springen
          </a>
          <Navigation />
          <main id="main" tabIndex={-1} className="flex-1 outline-none">
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/ueber-uns" element={<About />} />
                <Route path="/reitunterricht" element={<RidingLessons />} />
                <Route path="/reitunterricht/:id" element={<LessonOfferDetail />} />
                <Route path="/turniere" element={<Tournaments />} />
                <Route path="/aktivitaeten" element={<Activities />} />
                <Route path="/news/:id" element={<NewsDetail />} />
                <Route path="/galerie" element={<Gallery />} />
                <Route path="/einrichtungen" element={<Facilities />} />
                <Route path="/mitgliedschaft" element={<Membership />} />
                <Route path="/vorstand" element={<Board />} />
                <Route path="/sponsoren" element={<Sponsors />} />
                <Route path="/kontakt" element={<Contact />} />
                <Route path="/dokumente" element={<Documents />} />
                <Route path="/impressum" element={<Imprint />} />
                <Route path="/datenschutz" element={<Privacy />} />
                <Route path="/admin" element={<Admin />} />
                {/* Catch-all: 404 fuer alles, was sonst nirgends matcht. */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </main>
          <Footer />
          <BackToTop />
          <Toaster />
        </BrowserRouter>
      </ClubInfoProvider>
    </div>
  );
}

export default App;
